import { useEffect } from "react";

import { BANNER_HEIGHT_CSS_VAR } from "Components/NewUiBanner/NewUiBanner";

// Bug real (Trello "V2 | Arreglos de Responsive Design", 2026-09-04): el
// botón de "comunicarse con soporte" es el widget de HubSpot Conversations
// (script de terceros, ver App.js) — tiene position:fixed sin forma de
// reubicarlo, y a veces tapa otros elementos de la pantalla (ej. botones de
// acción en una esquina). No hay ningún JSX/CSS propio que controle su
// posición: HubSpot inyecta su propio contenedor
// (#hubspot-messages-iframe-container) directamente en document.body, de
// forma asincrónica (recién después de que su script externo carga).
//
// El contenedor es casi 100% un <iframe> de HubSpot (mismo origen del
// container, contenido cross-origin) — un mousedown sobre el widget mismo
// nunca llega a un listener puesto en el contenedor padre (el iframe se
// queda con el evento). Para poder arrastrar EL WIDGET ENTERO (no un handle
// aparte), se superpone un overlay transparente que cubre toda la burbuja,
// dentro del contenedor: captura el press antes que el iframe. Como el iframe
// no ve el click, un tap (sin arrastre) se reenvía abriendo el chat por la
// API de HubSpot (window.HubSpotConversations.widget.open, la misma que ya usa
// App.js). Se distingue tap de arrastre por un umbral de movimiento, y el
// overlay se desactiva mientras el chat está abierto para no tapar el panel.
export const SUPPORT_WIDGET_SELECTOR = "#hubspot-messages-iframe-container";
const STORAGE_KEY = "drapp_support_widget_position";
const OVERLAY_ATTR = "data-draggable-support-overlay";
const DRAG_HANDLE_ATTR = "data-draggable-support-handle";
const DRAG_MASK_ATTR = "data-draggable-support-mask";
const DRAG_THRESHOLD_PX = 6;

const clamp = (value, max) => Math.min(Math.max(value, 0), Math.max(max, 0));

const getPoint = (e) => (e.touches ? e.touches[0] : e);

// Bug real (Nacho, 2026-09-09, encontrado revisando el área tras los fixes
// de NewUiBanner): el widget arrancaba (o quedaba, si ya estaba arrastrado
// de antes) en cualquier posición, sin saber que el banner "Conocé la nueva
// DrApp" podía estar ocupando esa misma franja inferior — el z-index del
// iframe de HubSpot es más alto que el del banner (1080), así que tapaba
// sus botones por completo, del lado que fuera. En vez de perseguir "en qué
// lado está la burbuja hoy" (2 fixes ya lo hicieron, por reporte, para 2
// resoluciones distintas), el widget respeta el espacio que el banner ya
// expone en `--new-ui-banner-height` (mismo contrato que usan los modales).
function getReservedBottomSpace() {
  // Lectura directa del inline style, no getComputedStyle: NewUiBanner
  // siempre lo setea/saca como inline style de <html> (nunca vía hoja de
  // estilos), así que no hace falta resolver cascada — y evita depender del
  // soporte de custom properties en getComputedStyle bajo jsdom.
  const raw = document.documentElement.style.getPropertyValue(BANNER_HEIGHT_CSS_VAR);
  const px = parseFloat(raw);
  return Number.isFinite(px) ? px : 0;
}

function applyPosition(el, left, top) {
  const rect = el.getBoundingClientRect();
  const clampedLeft = clamp(left, window.innerWidth - rect.width);
  const clampedTop = clamp(top, window.innerHeight - rect.height - getReservedBottomSpace());
  // !important: el stylesheet propio de HubSpot posiciona el widget con
  // bottom/right (y podría tener su propia especificidad) — se neutraliza
  // seteando las 4 propiedades explícitamente.
  el.style.setProperty("left", `${clampedLeft}px`, "important");
  el.style.setProperty("top", `${clampedTop}px`, "important");
  el.style.setProperty("right", "auto", "important");
  el.style.setProperty("bottom", "auto", "important");
  return { left: clampedLeft, top: clampedTop };
}

function savePosition(left, top) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ left, top }));
  } catch {}
}

function getSavedPosition() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (saved && typeof saved.left === "number" && typeof saved.top === "number") return saved;
  } catch {}
  return null;
}

function restorePosition(el) {
  const saved = getSavedPosition();
  if (saved) {
    applyPosition(el, saved.left, saved.top);
    return true;
  }
  return false;
}

function openSupportChat() {
  try {
    window.HubSpotConversations?.widget?.open();
  } catch {}
}

// Máscara transparente a pantalla completa, viva sólo durante un arrastre en
// curso: el widget es un <iframe> cross-origin, así que si el puntero pasa por
// encima de OTRO iframe de la página mientras se arrastra, ese iframe se
// quedaría con los mousemove/mouseup y el widget quedaría "colgado" del mouse.
// La máscara garantiza que todos los eventos lleguen a los listeners.
function createDragMask() {
  const mask = document.createElement("div");
  mask.setAttribute(DRAG_MASK_ATTR, "true");
  Object.assign(mask.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483647",
    cursor: "move",
  });
  document.body.appendChild(mask);
  return mask;
}

// Abre el panel manteniendo, en lo posible, la esquina del botón de cerrar
// (abajo-derecha del panel) sobre la burbuja: así no se mueve al abrir ni al
// cerrar. El panel crece hacia arriba-izquierda desde la burbuja; sólo si con
// eso se saldría de la pantalla (burbuja pegada a un borde izquierdo/superior)
// se ancla por el borde opuesto y crece hacia el otro lado. applyPosition
// clampea contra el viewport + banner como red final.
function openPanelAnchoredToBubble(el, panelRect, bubbleW, bubbleH) {
  const saved = getSavedPosition();
  if (!saved) return;
  let left = saved.left + bubbleW - panelRect.width;
  if (left < 0) left = saved.left;
  let top = saved.top + bubbleH - panelRect.height;
  if (top < 0) top = saved.top;
  applyPosition(el, left, top);
}

// El overlay tapa TODA la burbuja para poder arrastrar el widget entero. Pero
// al abrir el chat, HubSpot agranda el contenedor a un panel completo — con tres
// consecuencias que se manejan acá:
//  1. Si el overlay lo siguiera tapando, bloquearía el chat → se desactiva
//     (pointer-events) mientras esté expandido y se reactiva al volver a
//     burbuja.
//  2. Con el overlay desactivado ya no se puede arrastrar → se muestra un handle
//     chico (barrita) sólo mientras el chat está abierto, para poder moverlo sin
//     bloquear el contenido.
//  3. El panel se abre anclado a la esquina de la burbuja (crece hacia adentro).
//     Al cerrarse, la burbuja queda donde está el botón de cerrar del chat —
//     esquina inferior-derecha del panel, en lo que HubSpot lo transforma — y
//     esa posición se persiste, así no salta a otro lado.
// Se compara contra el tamaño inicial (burbuja) en vez de un umbral fijo,
// porque no conocemos el tamaño exacto del launcher. No se desconecta el
// ResizeObserver: el widget vive toda la vida de la SPA (App.js no se
// desmonta), igual que el propio overlay.
function watchExpansion(el, overlay, dragHandle) {
  const launcher = el.getBoundingClientRect();
  const bubbleW = launcher.width;
  const bubbleH = launcher.height;
  let wasExpanded = false;
  let panelW = 0;
  let panelH = 0;
  const update = () => {
    const rect = el.getBoundingClientRect();
    const expanded = rect.width > bubbleW + 40 || rect.height > bubbleH + 40;
    overlay.style.pointerEvents = expanded ? "none" : "auto";
    dragHandle.style.display = expanded ? "block" : "none";
    if (expanded) {
      // Anclar sólo al abrir; si el panel crece después, sólo se reclampea para
      // no salirse (así no se pisa un arrastre del handle).
      if (!wasExpanded) openPanelAnchoredToBubble(el, rect, bubbleW, bubbleH);
      else applyPosition(el, rect.left, rect.top);
      const r = el.getBoundingClientRect();
      panelW = r.width;
      panelH = r.height;
      wasExpanded = true;
    } else if (wasExpanded) {
      wasExpanded = false;
      // La burbuja queda en la esquina inferior-derecha de donde estaba el panel
      // (el botón de cerrar del chat), y se persiste.
      const cur = el.getBoundingClientRect();
      const { left, top } = applyPosition(el, cur.left + panelW - bubbleW, cur.top + panelH - bubbleH);
      savePosition(left, top);
    }
  };
  update();
  if (typeof ResizeObserver === "function") {
    new ResizeObserver(update).observe(el);
  }
}

// Devuelve si restauró una posición guardada — tryBind sólo necesita
// reclampear el default de HubSpot cuando NO hubo restore (ese caso ya pasa
// por applyPosition, que ya clampea contra el espacio reservado).
function makeDraggable(el) {
  const restored = restorePosition(el);

  // startDrag maneja el arrastre desde cualquier target (el overlay de la
  // burbuja o el handle del chat abierto). onTap (opcional) corre sólo si se
  // soltó sin superar el umbral: el overlay lo usa para abrir el chat; el handle
  // no lo pasa (el chat ya está abierto). persist (default true) guarda la
  // posición: el overlay sí (mueve la burbuja); el handle no (sólo corre el
  // panel en la vista actual; la burbuja se reubica arrastrándola cerrada).
  const startDrag = (e, { onTap, persist = true } = {}) => {
    e.preventDefault();
    e.stopPropagation();
    const start = getPoint(e);
    const startX = start.clientX;
    const startY = start.clientY;
    const rect = el.getBoundingClientRect();
    const startLeft = rect.left;
    const startTop = rect.top;
    let dragging = false;
    let mask = null;

    const onMove = (moveEvent) => {
      const point = getPoint(moveEvent);
      const dx = point.clientX - startX;
      const dy = point.clientY - startY;
      // Umbral: por debajo sigue siendo un tap, no un arrastre.
      if (!dragging && Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) return;
      if (!dragging) {
        dragging = true;
        mask = createDragMask();
      }
      moveEvent.preventDefault();
      applyPosition(el, startLeft + dx, startTop + dy);
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onUp);
      window.removeEventListener("blur", onUp);
      if (mask) mask.remove();
      if (dragging) {
        if (persist) {
          const rect2 = el.getBoundingClientRect();
          savePosition(rect2.left, rect2.top);
        }
      } else if (onTap) {
        onTap();
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onUp);
    // Red de seguridad: si el foco se va de la ventana en medio del arrastre
    // (alt-tab, otra pestaña), se termina el drag igual y no queda pegado.
    window.addEventListener("blur", onUp);
  };

  // Overlay que cubre la burbuja cerrada: arrastrar reubica, tap abre el chat.
  const overlay = document.createElement("div");
  overlay.setAttribute(OVERLAY_ATTR, "true");
  overlay.setAttribute("role", "button");
  overlay.setAttribute("aria-label", "Botón de soporte: tocar para abrir el chat, arrastrar para reubicar");
  Object.assign(overlay.style, {
    position: "absolute",
    inset: "0",
    background: "transparent",
    // pointer (no "move"): la burbuja es ante todo un botón para abrir el chat;
    // el cursor de arrastre se muestra sólo durante el drag (en la máscara).
    cursor: "pointer",
    touchAction: "none",
    zIndex: "2147483647",
  });
  overlay.addEventListener("mousedown", (e) => startDrag(e, { onTap: openSupportChat }));
  overlay.addEventListener("touchstart", (e) => startDrag(e, { onTap: openSupportChat }), { passive: false });

  // Handle para mover el chat mientras está abierto (el overlay queda
  // desactivado para no bloquearlo). Barrita chica arriba-centro: no tapa el
  // título (izquierda) ni el botón de cerrar (derecha) del header de HubSpot.
  // Oculto mientras es burbuja; watchExpansion lo muestra al expandirse.
  const dragHandle = document.createElement("div");
  dragHandle.setAttribute(DRAG_HANDLE_ATTR, "true");
  dragHandle.setAttribute("role", "button");
  dragHandle.setAttribute("aria-label", "Mover la ventana de chat");
  dragHandle.title = "Arrastrar para mover";
  Object.assign(dragHandle.style, {
    position: "absolute",
    top: "6px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "44px",
    height: "5px",
    borderRadius: "3px",
    background: "rgba(0, 0, 0, 0.28)",
    cursor: "move",
    display: "none",
    touchAction: "none",
    zIndex: "2147483647",
  });
  dragHandle.addEventListener("mousedown", (e) => startDrag(e, { persist: false }));
  dragHandle.addEventListener("touchstart", (e) => startDrag(e, { persist: false }), { passive: false });

  el.appendChild(overlay);
  el.appendChild(dragHandle);
  watchExpansion(el, overlay, dragHandle);
  return restored;
}

function reclampCurrent() {
  const el = document.querySelector(SUPPORT_WIDGET_SELECTOR);
  if (!el || !el.querySelector(`[${OVERLAY_ATTR}]`)) return;
  const rect = el.getBoundingClientRect();
  const { left, top } = applyPosition(el, rect.left, rect.top);
  savePosition(left, top);
}

export default function useDraggableSupportWidget() {
  useEffect(() => {
    const tryBind = () => {
      const el = document.querySelector(SUPPORT_WIDGET_SELECTOR);
      if (!el || el.querySelector(`[${OVERLAY_ATTR}]`)) return; // ya ligado

      const restored = makeDraggable(el);
      // Sin una posición guardada, el widget queda en el default crudo de
      // HubSpot — que puede superponerse con el banner. Con una posición
      // restaurada, applyPosition ya clampeó contra el espacio reservado
      // (reclampear de nuevo acá releería el rect en medio del propio
      // cambio recién aplicado, redundante e innecesariamente frágil).
      if (!restored) reclampCurrent();
    };

    tryBind();

    // El script de HubSpot carga async/defer y recién crea su contenedor
    // cuando termina — un solo intento al montar llega tarde casi siempre.
    //
    // Bug real (Nacho, 2026-09-09, encontrado escribiendo los tests de
    // arriba sobre --new-ui-banner-height): reaccionar a CUALQUIER mutación
    // de <body> — sin mirar qué se agregó/sacó — hace que este observer
    // dispare por cualquier nodo que cualquier OTRA cosa monte ahí (un
    // modal, un portal, incluso los propios divs raíz de testing-library en
    // los tests), no sólo por el widget de HubSpot. Cada disparo de más es
    // un querySelector + posible escritura de estilos innecesaria — barato
    // una vez, pero una página con varios modales/portales puede generar
    // muchos por interacción. Se filtra a mutaciones que realmente agregan
    // o sacan el propio contenedor del widget.
    const touchesWidget = (mutationsList) =>
      mutationsList.some((m) =>
        [...m.addedNodes, ...m.removedNodes].some(
          (n) => n.nodeType === Node.ELEMENT_NODE && (n.matches?.(SUPPORT_WIDGET_SELECTOR) || n.querySelector?.(SUPPORT_WIDGET_SELECTOR))
        )
      );
    const bodyObserver = new MutationObserver((mutationsList) => {
      if (touchesWidget(mutationsList)) tryBind();
    });
    bodyObserver.observe(document.body, { childList: true });

    // NewUiBanner expone/actualiza `--new-ui-banner-height` mutando el
    // atributo `style` de <html> (aparece, cambia de alto por texto que
    // envuelve, o desaparece al dismissear) — cualquiera de esos cambios
    // puede abrir o cerrar espacio en la franja inferior donde vive el
    // widget, así que se reclampea contra el valor nuevo cada vez.
    const heightObserver = new MutationObserver(reclampCurrent);
    heightObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });

    window.addEventListener("resize", reclampCurrent);

    return () => {
      bodyObserver.disconnect();
      heightObserver.disconnect();
      window.removeEventListener("resize", reclampCurrent);
    };
  }, []);
}
